import {
    createSelector,
} from 'reselect';

import NAMESPACE from '../types';
import SLICE_NAMESPACE from './types';

const AB_TEST_PARAM = 'ab_test';
const AB_TEST_DELIMITER = ':';

/**
 * clean up wapi data before sending it to snowplow
 * @param {Array<Object>} definitions
 * @return {Array<Object>}
 */
function getSnowplowTreatments(definitions = []) {
    return definitions.map((definition) => {
        const {
            apply_test: _unused,
            ...otherProps
        } = definition;

        return otherProps;
    });
}

/**
 * @param {Object} experiment
 * @param {Boolean} experiment.apply_test
 * @param {Number} experiment.experiment_group
 * @return {String}
 */
function testVariantAsString(experiment) {
    const {
        apply_test: applyTest,
        experiment_group_name: experimentGroupName,
    } = experiment;

    return applyTest
        ? experimentGroupName
        : 'control';
}

const getOverrides = (appState) => appState[NAMESPACE][SLICE_NAMESPACE].overrides;
const getParams = (appState) => {
    const params = appState[NAMESPACE][SLICE_NAMESPACE].params;

    const arr = Object.values(params);

    return arr;
};
const getVisitorId = (appState) => appState[NAMESPACE][SLICE_NAMESPACE].visitorId;
const getCachedParams = (appState) => appState[NAMESPACE][SLICE_NAMESPACE].cachedParams;

const extractOverrides = (queryParams = {}) => {
    let queryOverrides = queryParams[AB_TEST_PARAM];
    if (!queryOverrides) {
        return {};
    }

    queryOverrides = typeof queryOverrides === 'string'
        ? [queryOverrides]
        : queryOverrides;

    return queryOverrides.reduce((obj, override) => {
        const [testName, variantName] = override.split(AB_TEST_DELIMITER);
        if (!testName || !variantName) {
            return obj;
        }

        return {
            ...obj,
            [testName]: variantName,
        };
    }, {});
};

const applyOverrides = (experiments = [], testOverrides = {}) => {
    /* wapi provides data as an array of objects:
    * [ { name: <test1>, experiment_group: <group1> }, { name: <test2>, experiment_group: <group2> } ,... ]
    *
    * but we want it as a regular ol' map:
    * { <test1>: <group1>, <test2>: <group2> ,... }
    */
    const tests = experiments.reduce((testParams, experiment) => ({
        ...testParams,
        [experiment.name]: testVariantAsString(experiment),
    }), {});

    return {
        /* apply overrides specified as url query params */
        ...tests,
        ...extractOverrides(testOverrides),
    };
};

const selectTestData = createSelector(
    getParams,
    getOverrides,
    applyOverrides,
);

const selectVariant = (testData, testName) => {
    if (!testData || !testData[testName]) {
        return 'control';
    }

    return testData[testName];
};

const selectTestVariant = createSelector(
    selectTestData,
    (appState, testName) => testName,
    selectVariant,
);

const selectCachedTestVariant = createSelector(
    getCachedParams,
    (appState, testName) => testName,
    selectVariant,
);

const selectHasActiveTestVariant = createSelector(
    selectTestData,
    (testData) => {
        return Object.values(testData)
            .some((bucketName) => (bucketName !== 'control' && bucketName !== 'not_participating'));
    },
);

const selectTestDefinitions = getParams;

const selectTestOverrides = createSelector(
    getOverrides,
    extractOverrides,
);

const selectVisitorId = getVisitorId;

const selectSnowplowTestAssignedEventData = createSelector(
    selectTestDefinitions,
    selectVisitorId,
    (definitions, visitorId) => ({
        action: 'assigned',
        category: 'split_test',
        property: {
            treatments: getSnowplowTreatments(definitions)
                .sort((d1, d2) => d1.id - d2.id) // data team requirement
                .filter((d1) => !d1.do_not_track),
            visitorId,
        },
    }),
);

const selectSnowplowTestRenderedEventData = createSelector(
    (appState, testName) => testName,
    selectTestVariant,
    selectTestDefinitions,
    selectVisitorId,
    (name, variant, definitions, visitorId) => ({
        action: 'rendered',
        category: 'split_test',
        label: `${name}:${variant}`,
        property: {
            treatments: getSnowplowTreatments(definitions)
                .sort((d1, d2) => d1.id - d2.id) // data team requirement
                .filter((d1) => !d1.do_not_track),
            visitorId,
        },
    }),
);

export {
    selectCachedTestVariant,
    selectHasActiveTestVariant,
    selectSnowplowTestAssignedEventData,
    selectSnowplowTestRenderedEventData,
    selectTestData,
    selectTestDefinitions,
    selectTestOverrides,
    selectTestVariant,
    selectVisitorId,
};

const splitTestSelectors = {
    selectCachedTestVariant,
    selectHasActiveTestVariant,
    selectSnowplowTestAssignedEventData,
    selectSnowplowTestRenderedEventData,
    selectTestData,
    selectTestDefinitions,
    selectTestOverrides,
    selectTestVariant,
    selectVisitorId,
};

export default splitTestSelectors;
